
import React, { useState } from 'react';
import { confirmLogoutWithUnsent } from './utils/logoutGuard';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import {
  FiBarChart2,
  FiTrendingUp,
  FiGrid,
  FiSettings,
  FiUser,
  FiLogOut,
  FiFileText,
  FiHelpCircle,
  FiAward
} from 'react-icons/fi';
import { GiGolfTee } from 'react-icons/gi';
import APP_VERSION from './version';
import { preloadRoute } from './routePreload';
import './MainMenu.css';

// `legacy` keeps the original offset for roots that still use the taller
// .top-blue-bar. `inline` takes the toggle OUT of fixed positioning so a page
// can place it in its own header — Home has no appbar to hang it off, and a
// fixed toggle there would land on top of the 1-19 mark and then float over the
// cards on any viewport short enough to scroll.
const MainMenu = ({ legacy = false, inline = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const { logout, userProfile, currentSociety } = useAuth();
  const navigate = useNavigate();

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  const handleLogout = async (e) => {
    e.preventDefault();
    toggleMenu();
    if (!(await confirmLogoutWithUnsent())) return;
    await logout();
    navigate('/login');
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  // Find society player profile
  const societyData = userProfile?.societies?.find(
    s => String(s.id) === String(currentSociety?.id)
  );
  const playerProfile = societyData?.player_profile;
  const handicap = playerProfile?.handicap;

  const NavItem = ({ to, icon, label, delay }) => (
    <NavLink
      to={to}
      className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
      onClick={toggleMenu}
      // Most screens behind this drawer are lazy chunks, so without this the
      // tap is followed by a download before anything can render. Starting it
      // as the finger goes down buys the whole press, and the drawer's own
      // close animation on top of that.
      onPointerDown={() => preloadRoute(to)}
      style={{ animationDelay: `${delay}ms` }}
    >
      <div className="nav-item-active-bar" />
      <div className="nav-item-icon">{icon}</div>
      <span>{label}</span>
    </NavLink>
  );

  return (
    <>
      <div className={'menu-toggle-container'
        + (legacy ? ' menu-toggle-container--legacy' : '')
        + (inline ? ' menu-toggle-container--inline' : '')}>
        <button
          type="button"
          className="hamburger-icon"
          onClick={toggleMenu}
          aria-label="Menu"
          aria-expanded={isOpen}
        >
          <div className="bar"></div>
          <div className="bar"></div>
          <div className="bar"></div>
        </button>
      </div>

      <div className={`drawer-overlay ${isOpen ? 'open' : ''}`} onClick={toggleMenu}></div>

      <div className={`premium-drawer ${isOpen ? 'open' : ''}`}>
        <div className="drawer-header-hero">
          <div className="hero-avatar">
            {getInitials(userProfile?.display_name)}
          </div>
          <div className="hero-player-info">
            <span className="hero-player-name">{userProfile?.display_name || 'Golfer'}</span>
            <span className="hero-player-sub">
              Hcp {handicap != null ? handicap.toFixed(1) : '—'} &bull; {currentSociety?.name || 'Drift Original'}
            </span>
          </div>
        </div>

        <div className="drawer-nav-content">
          <div className="nav-section">
            <div className="nav-section-label">PLAY</div>
            <NavItem to="/home" icon={<FiGrid size={20} />} label="Home" delay={40} />
            <NavItem to="/play" icon={<GiGolfTee size={20} />} label="Play Golf" delay={80} />
            <NavItem to="/leaderboard" icon={<FiBarChart2 size={20} />} label="Leaderboard" delay={120} />
            <NavItem to="/tournaments/scorecard-entry" icon={<FiGrid size={20} />} label="Rounds" delay={160} />
            <NavItem to="/order-of-merit" icon={<FiAward size={20} />} label="Order of Merit" delay={200} />
          </div>

          <div className="nav-section">
            <div className="nav-section-label">ANALYTICS</div>
            <NavItem to="/insights" icon={<FiTrendingUp size={20} />} label="Insights" delay={200} />
            <NavItem to="/betting" icon={<FiBarChart2 size={20} />} label="Predictions" delay={240} />
          </div>

          <div className="nav-section">
            <div className="nav-section-label">ACCOUNT</div>
            <NavItem to="/profile" icon={<FiUser size={20} />} label="My Profile" delay={280} />
            <NavItem to="/setup" icon={<FiSettings size={20} />} label="Manage" delay={320} />
            <NavItem to="/support" icon={<FiHelpCircle size={20} />} label="Support" delay={360} />
            <NavItem to="/legal" icon={<FiFileText size={20} />} label="Terms & Privacy" delay={400} />

            <button
              className="nav-item logout-item" 
              onClick={handleLogout}
              style={{ animationDelay: '440ms' }}
            >
              <div className="nav-item-icon"><FiLogOut size={20} /></div>
              <span>Log Out</span>
            </button>
          </div>
        </div>

        <div className="drawer-footer-version">Version: {APP_VERSION}</div>
      </div>
    </>
  );
};

export default MainMenu;
